import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const META_TITLE = brandedTitle("Privacy Policy");
const META_DESC = safeDesc(
  "How Heallexa Inc. collects, uses, and protects personal data for patients and providers, including your rights and how to request data deletion."
);

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: "https://heallexa.com/privacy" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://heallexa.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background pb-1">
      <Navbar />
      <main className="pt-28 pb-16 mx-auto max-w-3xl px-6">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Effective date: May 22, 2026</p>

        <div className="rounded-lg border border-border bg-card p-4 mb-8 text-sm space-y-2">
          <p><strong>Emergency notice.</strong> Heallexa does not provide emergency medical services. If you are experiencing a medical emergency, call 911 immediately.</p>
          <p><strong>Medical disclaimer.</strong> Heallexa does not provide medical advice, diagnosis, or treatment. Always consult a qualified healthcare provider.</p>
        </div>

        <div className="space-y-8 text-foreground/90 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold mb-2">1. Introduction</h2>
            <p>This Privacy Policy explains how Heallexa Inc. ("Heallexa," "we," "us") collects, uses, and protects information when you use https://heallexa.com (the "Service"). By using the Service you agree to this policy.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">2. Information we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account information:</strong> name, email, phone number, password, and role (patient or provider).</li>
              <li><strong>Provider listing information:</strong> practice name, specialties, locations, insurance accepted, photos, biographies, hours, and other professional details.</li>
              <li><strong>Patient activity:</strong> searches, saved providers, contact requests, and reviews you submit.</li>
              <li><strong>Device and usage data:</strong> IP address, browser type, pages viewed, referring URLs, and approximate location derived from IP.</li>
              <li><strong>Cookies and similar technologies</strong> used to keep you signed in, remember preferences, and measure performance.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">3. How we use information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Operate, maintain, and improve the Service.</li>
              <li>Verify provider identity and listing accuracy.</li>
              <li>Communicate about your account, security, and product updates.</li>
              <li>Detect, prevent, and respond to fraud, abuse, and security incidents.</li>
              <li>Comply with legal obligations.</li>
            </ul>
            <p className="mt-3">We do not sell your personal information.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">4. How we share information</h2>
            <p>We share information only with: (a) service providers who help us run the Service (hosting, email delivery, analytics, payment processing) under confidentiality obligations; (b) other users, when you choose to make information public (such as a provider listing); and (c) authorities when required by law or to protect rights and safety.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">5. Data retention</h2>
            <p>We keep personal information for as long as your account is active or as needed to provide the Service, resolve disputes, and meet legal obligations. You may request deletion at any time.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">6. Security</h2>
            <p>We use industry-standard technical and organizational safeguards including encryption in transit, access controls, and audit logging. No method of transmission or storage is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">7. Your rights</h2>
            <p>Depending on where you live, you may have the right to access, correct, export, or delete your personal information, and to object to or restrict certain processing. To exercise these rights, contact us at <a className="underline" href="mailto:contact@heallexa.com">contact@heallexa.com</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">8. Provider listing corrections and removal</h2>
            <p>If you are a healthcare provider and want to correct, claim, or remove a listing about you, email <a className="underline" href="mailto:contact@heallexa.com">contact@heallexa.com</a> from an address associated with your practice. We will verify your identity and respond promptly.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">9. Children</h2>
            <p>The Service is not directed to children under 13, and we do not knowingly collect information from them. If you believe a child has provided us information, contact us so we can delete it.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">10. International users</h2>
            <p>The Service is operated from the United States. If you access it from another country, you consent to your information being processed in the United States.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">11. Changes to this policy</h2>
            <p>We may update this policy from time to time. When we do, we will revise the effective date above and, if changes are material, provide additional notice.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">12. Contact</h2>
            <p>Heallexa Inc. — <a className="underline" href="mailto:contact@heallexa.com">contact@heallexa.com</a> — <a className="underline" href="https://heallexa.com">https://heallexa.com</a></p>
          </section>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
